import { styled } from 'goober';
import Markdown from 'markdown-to-jsx';
import { useEffect, useState, useRef, useMemo } from 'preact/hooks';

import ChevronDownIcon from '~/assets/chevron-down.svg?react';
import SendIcon from '~/assets/send-icon.svg?react';
import CloseIcon from '~/assets/close-icon.svg?react';
import { resolveHostPageLinkTarget } from '~/utils';

import { BTN_HEIGHT } from './FabButton';

import type { WidgetZIndexLayers } from '../utils/zIndexLayers';
import type { ComponentChildren, ComponentProps } from 'preact';
import type { LinkTarget, NudgeInfo, WorknetWidgetConfig } from '@worknet/models';

type Side = WorknetWidgetConfig['styles']['side'];

const GAP = 16;

const Base = styled('div')<{ baseZIndex?: number }>`
  color: var(--clr-primary);
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid var(--clr-primary);
  box-shadow:
    rgba(50, 50, 93, 0.25) 0 6px 12px -2px,
    rgba(0, 0, 0, 0.3) 0 3px 7px -3px;
  padding: 20px;
  z-index: ${(p) => p.baseZIndex ?? 3};
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
`;

const CenterContainer = styled(Base)<{
  maxWidth?: string;
  side: Side;
  closing?: boolean;
}>`
  position: fixed;
  left: 50%;
  bottom: ${GAP}px;
  transform: translateX(-50%);
  width: min(90vw, ${(p) => p.maxWidth || '480px'});
  will-change: transform, opacity;
  ${(p) =>
    p.closing
      ? `
      transform-origin: 50% 100%;
      animation: nudgeSlideDownCenter 320ms cubic-bezier(0.4,0,0.2,1) forwards;
    `
      : `
      opacity: 0;
      transform-origin: 50% 100%;
      transform: translateX(-50%) translateY(40px) scale(0.92);
      animation: nudgeSlideUpCenter 350ms cubic-bezier(0.34,1.56,0.64,1) 400ms forwards;
    `}

  @keyframes nudgeToButtonRight {
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(52px) scale(0);
    }
  }
  @keyframes nudgeToButtonLeft {
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(52px) scale(0);
    }
  }
  @keyframes nudgeSlideUpCenter {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(40px) scale(0.92);
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }
  }
  @keyframes nudgeSlideDownCenter {
    0% {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateX(-50%) translateY(40px) scale(0.94);
    }
  }
  @keyframes nudgeFromButtonRight {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(52px) scale(0);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }
  }
  @keyframes nudgeFromButtonLeft {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(52px) scale(0);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }
  }
`;

const CloseNudgeButton = styled('button')<{ side?: Side; zIndex?: number; borderZIndex?: number }>`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: var(--clr-primary);
  background: var(--clr-secondary);
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
  z-index: ${(p) => p.zIndex ?? 10};

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border: 1px solid var(--clr-primary);
    border-radius: 50%;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 60% 100%, 0 40%);
    pointer-events: none;
    z-index: ${(p: { borderZIndex?: number }) => p.borderZIndex ?? 1};
  }

  svg {
    width: 12px;
    height: 12px;
    display: block;
  }

  &:hover::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--clr-primary);
    opacity: 0.11;
    border-radius: 50%;
    pointer-events: none;
    box-shadow:
      rgba(50, 50, 93, 0.25) 0 2px 8px -2px,
      rgba(0, 0, 0, 0.3) 0 2px 4px -2px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const InlineContainer = styled(Base)<{
  side: Side;
  aboveButton?: boolean;
  buttonHasText?: boolean;
  maxWidth?: string;
  closing?: boolean;
}>`
  position: absolute;
  ${(p) => (p.side === 'left' ? 'left: 0;' : 'right: 0;')}
  bottom: ${(p) => (p.aboveButton ? `${BTN_HEIGHT + GAP}px` : '0')};
  width: min(45vw, ${(p) => p.maxWidth || '320px'});
  max-width: min(45vw, ${(p) => p.maxWidth || '360px'});
  max-width: ${(p) => p.maxWidth || '320px'};

  ${(p) =>
    p.aboveButton
      ? `
    box-shadow: none;
    filter: drop-shadow(rgba(50, 50, 93, 0.15) 0px 4px 8px) drop-shadow(rgba(0, 0, 0, 0.2) 0px 2px 4px);
    
    &::before {
      content: '';
      position: absolute;
      top: 100%;
      ${
        p.side === 'left'
          ? `left: ${p.buttonHasText ? 44 : 28}px;`
          : `right: ${p.buttonHasText ? 43 : 28}px;`
      }
      width: 0;
      height: 0;
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-top: 9px solid var(--clr-primary);
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      ${
        p.side === 'left'
          ? `left: ${p.buttonHasText ? 45 : 29}px;`
          : `right: ${p.buttonHasText ? 45 : 29}px;`
      }
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #ffffff;
    }
  `
      : ''}

  ${(p) =>
    p.closing
      ? `
      transform-origin: bottom ${p.side === 'left' ? 'left' : 'right'};
      animation: nudgeToButtonInline 300ms cubic-bezier(0.4,0,0.2,1) forwards;
    `
      : `
      opacity: 0;
      transform-origin: bottom ${p.side === 'left' ? 'left' : 'right'};
      transform: translateY(12px) scale(0.85);
      animation: nudgeFromButtonInline 320ms cubic-bezier(0.34,1.56,0.64,1) 400ms forwards;
    `}

  @keyframes nudgeToButtonInline {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(12px) scale(0.85);
    }
  }
  @keyframes nudgeFromButtonInline {
    0% {
      opacity: 0;
      transform: translateY(12px) scale(0.85);
    }
    60% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const SideContainer = styled(Base)<{
  side: Side;
  hasButton?: boolean;
  maxWidth?: string;
  closing?: boolean;
}>`
  position: absolute;
  ${(p) => (p.side === 'left' ? 'left: 0;' : 'right: 0;')}
  bottom: ${(p) => (p.hasButton ? `${BTN_HEIGHT + 8}px` : '0')};
  width: min(45vw, ${(p) => p.maxWidth || '360px'});
  max-width: min(45vw, ${(p) => p.maxWidth || '360px'});
  max-width: ${(p) => p.maxWidth || '360px'};

  ${(p) =>
    p.closing
      ? `
      transform-origin: bottom ${p.side === 'left' ? 'left' : 'right'};
      animation: nudgeToButtonSide 300ms cubic-bezier(0.4,0,0.2,1) forwards;
    `
      : `
      opacity: 0;
      transform-origin: bottom ${p.side === 'left' ? 'left' : 'right'};
      transform: translateY(20px) scale(0.88);
      animation: nudgeFromButtonSide 340ms cubic-bezier(0.34,1.56,0.64,1) 400ms forwards;
    `}

  @keyframes nudgeToButtonSide {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(20px) scale(0.88);
    }
  }
  @keyframes nudgeFromButtonSide {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.88);
    }
    60% {
      opacity: 1;
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const NudgeStack = styled('div')`
  display: grid;
  gap: 18px;
  width: 100%;
`;

const InputField = styled('input')`
  width: 100%;
  flex: 1 1 auto;
  box-sizing: border-box;
  padding: 10px 14px;
  background: #fff;
  border: 0 none;
  border-radius: 4px 0 0 4px;
  color: #111;
  font-size: 14px;
  outline: none;
  box-shadow: none;
  &:focus,
  &:focus-visible {
    border: 0 none;
    outline: none;
    box-shadow: none;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const InputRow = styled('div')<{ focusZIndex?: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  background: #ffffff;
  border: 1px solid var(--borders-color, #828282);
  border-radius: 4px;
  padding: 0 !important;

  &:focus-within {
    z-index: ${(p) => p.focusZIndex ?? 1};
    border-color: rgb(49, 130, 206);
    box-shadow: rgb(49, 130, 206) 0px 0px 7px 0px;
  }
`;

const SendButton = styled('button')`
  appearance: none;
  border: 0;
  background: var(--clr-primary);
  color: var(--clr-tertiary);
  display: inline-flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0 3px 3px 0;
  padding: 10px 14px;
  font-weight: 600;
  font-size: 14px;
  transition:
    filter 160ms ease,
    opacity 160ms ease,
    background-color 160ms ease;
  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    filter: brightness(0.95);
  }
`;

const QuickRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const QuickBtn = styled('button')<{ bgZIndex?: number }>`
  appearance: none;
  background: var(--clr-secondary);
  color: var(--clr-primary);
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 8px 12px;
  border: 2px solid var(--clr-primary);
  cursor: pointer;
  position: relative;
  max-width: 100%;
  overflow: hidden;
  transition: border-color 0.3s ease;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    background: var(--clr-primary);
    transform: translateY(100%);
    transition: transform 0.5s ease;
    z-index: ${(p) => p.bgZIndex ?? -1};
    border-radius: 8px;
  }

  &:hover::before {
    transform: translateY(0);
  }

  &:hover {
    color: var(--clr-secondary);
    border-color: var(--clr-primary);
  }

  &:active::before {
    transform: translateY(0);
  }

  &:active {
    color: var(--clr-secondary);
  }

  &:disabled {
    background: #e3e3e3 !important;
    color: #828282 !important;
    border-color: #e3e3e3 !important;
    cursor: not-allowed;
  }

  &:disabled::before {
    display: none;
  }
`;

const MarkdownContainer = styled('div')`
  max-width: 100%;
  overflow-wrap: anywhere;

  * {
    color: #000;
    padding: 0;
    margin: 0;
  }

  pre {
    max-width: 100%;
    overflow-x: auto;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 12px;
  }

  pre code {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  :not(pre) > code {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 1px 4px;
    font-size: 0.9em;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }

  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 4px 8px;
    border: 1px solid #e5e7eb;
    overflow-wrap: break-word;
  }

  img {
    max-width: 100%;
  }
`;

const CloseButton = styled('button')<{ side: Side; zIndex?: number; borderZIndex?: number }>`
  position: absolute;
  top: -12px;
  ${(p) => (p.side === 'left' ? 'left: -12px;' : 'right: -12px;')}
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: var(--clr-primary);
  background: var(--clr-secondary);
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
  z-index: ${(p) => p.zIndex ?? 10};

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border: 1px solid var(--clr-primary);
    border-radius: 50%;
    ${(p) =>
      p.side === 'left'
        ? 'clip-path: polygon(0 0, 100% 0, 100% 40%, 40% 100%, 0 100%);'
        : 'clip-path: polygon(0 0, 100% 0, 100% 100%, 60% 100%, 0 40%);'}
    pointer-events: none;
    z-index: ${(p: { borderZIndex?: number }) => p.borderZIndex ?? 1};
  }

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--clr-primary);
    opacity: 0.11;
    border-radius: 50%;
    pointer-events: none;
    box-shadow:
      rgba(50, 50, 93, 0.25) 0 2px 8px -2px,
      rgba(0, 0, 0, 0.3) 0 2px 4px -2px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

export type NudgeBoxProps = {
  side: Side;
  maxWidthCenter: string;
  maxWidthSide: string;
  maxWidthTooltip: string;
  gap?: number;
  nudgeInfo?: NudgeInfo;
  buttonHasText: boolean;
  onDismiss?: () => void;
  shouldClose?: boolean;
  onUserInput?: (input: string, display?: string) => void;
  zIndexLayers?: WidgetZIndexLayers;
  linkTarget?: LinkTarget;
} & Omit<ComponentProps<'div'>, 'ref'>;

function NudgeContent({
  message,
  placeholder,
  quickInputs,
  onUserInput,
  zIndexLayers,
  linkTarget = '_blank',
}: {
  message?: string;
  placeholder?: string;
  quickInputs?: Array<{ display?: string; input: string }>;
  onUserInput: NudgeBoxProps['onUserInput'];
  zIndexLayers?: WidgetZIndexLayers;
  linkTarget?: LinkTarget;
}) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // A component override rather than static `props`, so `auto` can resolve the
  // target per href.
  const markdownOptions = useMemo(
    () => ({
      overrides: {
        a: {
          component: ({
            href,
            children,
            ...props
          }: { href?: string; children?: ComponentChildren } & Record<string, unknown>) => (
            <a href={href} {...resolveHostPageLinkTarget(linkTarget, href)} {...props}>
              {children}
            </a>
          ),
        },
      },
    }),
    [linkTarget]
  );

  return (
    <NudgeStack>
      {message && (
        <MarkdownContainer>
          <Markdown options={markdownOptions}>{message}</Markdown>
        </MarkdownContainer>
      )}
      {quickInputs && quickInputs.length > 0 && (
        <QuickRow>
          {quickInputs.map((q, i) => (
            <QuickBtn
              key={i}
              type="button"
              title={q.display || q.input}
              onClick={() => onUserInput?.(q.input, q.display)}
              bgZIndex={zIndexLayers?.nudgeBox.quickButtonBg}
            >
              {q.display || q.input}
            </QuickBtn>
          ))}
        </QuickRow>
      )}
      {placeholder && (
        <InputRow focusZIndex={zIndexLayers?.nudgeBox.inputOverlay}>
          <InputField
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onInput={(e) => setInputValue((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputValue.trim()) {
                onUserInput?.(inputValue);
              }
            }}
          />
          <SendButton type="button" aria-label="Send" onClick={() => onUserInput?.(inputValue)}>
            <SendIcon />
          </SendButton>
        </InputRow>
      )}
    </NudgeStack>
  );
}

export function NudgeBox({
  nudgeInfo,
  side,
  maxWidthCenter,
  maxWidthSide,
  buttonHasText,
  onDismiss,
  onUserInput,
  shouldClose,
  zIndexLayers,
  linkTarget,
  ...rest
}: NudgeBoxProps) {
  const [closing, setClosing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) {
    return null;
  }

  const box = nudgeInfo && 'box' in nudgeInfo && nudgeInfo?.box ? nudgeInfo?.box : undefined;
  const button =
    nudgeInfo && 'button' in nudgeInfo && nudgeInfo?.button ? nudgeInfo?.button : undefined;
  const boxAlign =
    nudgeInfo && 'boxAlign' in nudgeInfo && nudgeInfo?.boxAlign ? nudgeInfo?.boxAlign : undefined;

  const handleClose = () => {
    setClosing(true);
    // Match animation duration (320ms center, 300ms side) with a small buffer
    const timeout = boxAlign === 'center' ? 340 : 320;
    window.setTimeout(() => {
      if (onDismiss) {
        onDismiss();
      }
      setDismissed(true);
    }, timeout);
  };

  useEffect(() => {
    if (shouldClose && !closing && !dismissed) {
      handleClose();
    }
  }, [shouldClose]);

  if (boxAlign === 'center') {
    return (
      <CenterContainer
        side={side}
        maxWidth={maxWidthCenter}
        closing={closing}
        baseZIndex={zIndexLayers?.nudge}
        {...rest}
      >
        {!button && (
          <CloseNudgeButton
            type="button"
            aria-label="Close nudge"
            onClick={handleClose}
            zIndex={zIndexLayers?.nudgeControls}
            borderZIndex={zIndexLayers?.nudgeBox.closeBorder}
          >
            <ChevronDownIcon width={16} height={11} />
          </CloseNudgeButton>
        )}
        <NudgeContent
          message={box?.message}
          placeholder={box?.input?.placeholder}
          quickInputs={box?.quickInputs}
          onUserInput={onUserInput}
          zIndexLayers={zIndexLayers}
          linkTarget={linkTarget}
        />
      </CenterContainer>
    );
  }

  if (button) {
    return (
      <InlineContainer
        side={side}
        aboveButton={!!button}
        buttonHasText={buttonHasText}
        maxWidth={maxWidthSide}
        closing={closing}
        baseZIndex={zIndexLayers?.nudge}
        {...rest}
      >
        <CloseButton
          side={side}
          type="button"
          aria-label="Close"
          onClick={handleClose}
          zIndex={zIndexLayers?.nudgeControls}
          borderZIndex={zIndexLayers?.nudgeBox.closeBorder}
        >
          <CloseIcon />
        </CloseButton>
        <NudgeContent
          message={box?.message}
          placeholder={box?.input?.placeholder}
          quickInputs={box?.quickInputs}
          onUserInput={onUserInput}
          zIndexLayers={zIndexLayers}
          linkTarget={linkTarget}
        />
      </InlineContainer>
    );
  }
  if (box && boxAlign === 'side') {
    return (
      <SideContainer
        side={side}
        hasButton={!!button}
        maxWidth={maxWidthSide}
        closing={closing}
        baseZIndex={zIndexLayers?.nudge}
        {...rest}
      >
        {!button && (
          <CloseNudgeButton
            type="button"
            aria-label="Close nudge"
            onClick={handleClose}
            zIndex={zIndexLayers?.nudgeControls}
            borderZIndex={zIndexLayers?.nudgeBox.closeBorder}
          >
            <ChevronDownIcon width={16} height={11} />
          </CloseNudgeButton>
        )}
        <NudgeContent
          message={box?.message}
          placeholder={box?.input?.placeholder}
          quickInputs={box?.quickInputs}
          onUserInput={onUserInput}
          zIndexLayers={zIndexLayers}
          linkTarget={linkTarget}
        />
      </SideContainer>
    );
  }
  return null;
}
